import type { LookalikeView } from '@repo/contracts';
import {
  mulberry32,
  projectTo2D,
  standardize,
  toFeatureVector,
} from '@repo/persona-lookalikes/web';

/** Power iteration converges to the same dominant eigenvectors regardless of
 *  its random starting vector — this seed only affects convergence speed, not
 *  the answer, so a fixed literal is fine (and keeps the projection
 *  reproducible run to run). */
const PROJECTION_SEED = 1;

/**
 * The similarity-view scatter positions: real PCA (`projectTo2D`, from
 * `@repo/persona-lookalikes`) over every field at once, rescaled from
 * component space into pixel space. See `LookalikeSchema`'s own doc on why
 * this is the view that needs a projection and the field view doesn't.
 */
export function similarityPositions(
  view: LookalikeView,
  pixelX: { max: number; min: number },
  pixelY: { max: number; min: number },
): { x: number; y: number }[] {
  const vectors = view.lookalikes.map((lookalike) =>
    toFeatureVector(view.spec.schema, lookalike),
  );
  const { standardized } = standardize(vectors);
  const projected = projectTo2D(standardized, mulberry32(PROJECTION_SEED));
  if (projected.length === 0) return [];

  const xs = projected.map((p) => p.x);
  const ys = projected.map((p) => p.y);
  const xMin = Math.min(...xs);
  const xMax = Math.max(...xs);
  const yMin = Math.min(...ys);
  const yMax = Math.max(...ys);
  const xSpan = xMax - xMin || 1;
  const ySpan = yMax - yMin || 1;

  return projected.map((p) => ({
    x: pixelX.min + ((p.x - xMin) / xSpan) * (pixelX.max - pixelX.min),
    y: pixelY.min + ((p.y - yMin) / ySpan) * (pixelY.max - pixelY.min),
  }));
}
